import { EnumAutomationAction } from '../enums/automation-action.enum';
import { EnumAutomationProvisioningStatus } from '../enums/automation-provisioning-status.enum';
import { EnumAutomationRunStatus } from '../enums/automation-run-status.enum';
import { EnumAutomationSchedulePreset } from '../enums/automation-schedule-preset.enum';
import { EnumAutomationStatus } from '../enums/automation-status.enum';

/* eslint-disable @typescript-eslint/naming-convention */
export interface IAutomationScheduleRaw {
  preset: string;
  days_of_week?: number[];
  day_of_week?: number;
  day_of_month?: number;
  time: string;
  timezone: string;
  human_description?: string;
  next_run_at?: string | null;
}

export interface IAutomationLastRunRaw {
  status: string;
  finished_at: string | null;
}

export interface IAutomationRaw {
  id: string;
  title: string;
  description: string;
  status: string;
  provisioning_status: string;
  provisioning_error: string | null;
  schedule: IAutomationScheduleRaw;
  next_run_at: string | null;
  last_run: IAutomationLastRunRaw | null;
  allowed_actions: string[];
  version: number;
  created_at: string;
  updated_at: string;
  paused_at: string | null;
}

export interface IAutomationListRaw {
  items: IAutomationRaw[];
}

export interface IAutomationRunAcceptedRaw {
  id: string;
  automation_id: string;
  status: string;
  queued_at: string;
}
/* eslint-enable @typescript-eslint/naming-convention */

export interface IAutomationSchedule {
  preset: EnumAutomationSchedulePreset;
  daysOfWeek: number[];
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  time: string;
  timezone: string;
  humanDescription: string;
  nextRunAt: string | null;
}

export interface IAutomationLastRun {
  status: EnumAutomationRunStatus;
  finishedAt: string | null;
}

export interface IAutomation {
  id: string;
  title: string;
  description: string;
  status: EnumAutomationStatus;
  provisioningStatus: EnumAutomationProvisioningStatus;
  provisioningError: string | null;
  schedule: IAutomationSchedule;
  nextRunAt: string | null;
  lastRun: IAutomationLastRun | null;
  allowedActions: EnumAutomationAction[];
  version: number;
  createdAt: string;
  updatedAt: string;
  pausedAt: string | null;
}

export interface IAutomationList {
  items: IAutomation[];
}

export interface IAutomationRunAccepted {
  id: string;
  automationId: string;
  status: string;
  queuedAt: string;
}

export interface IAutomationScheduleRequest {
  preset: EnumAutomationSchedulePreset;
  daysOfWeek?: number[];
  dayOfWeek?: number;
  dayOfMonth?: number;
  time: string;
  timezone: string;
}

export interface ICreateAutomationRequest {
  title: string;
  description: string;
  schedule: IAutomationScheduleRequest;
  active: boolean;
}

export interface IUpdateAutomationRequest {
  version: number;
  title?: string;
  description?: string;
  schedule?: IAutomationScheduleRequest;
}

const toStatus = (raw: string): EnumAutomationStatus =>
  raw === EnumAutomationStatus.Active || raw === EnumAutomationStatus.Paused
    ? (raw as EnumAutomationStatus)
    : EnumAutomationStatus.Unknown;

const toProvisioningStatus = (
  raw: string
): EnumAutomationProvisioningStatus =>
  raw === EnumAutomationProvisioningStatus.Syncing ||
  raw === EnumAutomationProvisioningStatus.Ready ||
  raw === EnumAutomationProvisioningStatus.Error
    ? (raw as EnumAutomationProvisioningStatus)
    : EnumAutomationProvisioningStatus.Unknown;

const toRunStatus = (raw: string): EnumAutomationRunStatus =>
  (Object.values(EnumAutomationRunStatus) as string[]).includes(raw)
    ? (raw as EnumAutomationRunStatus)
    : EnumAutomationRunStatus.Unknown;

const toPreset = (raw: string): EnumAutomationSchedulePreset =>
  raw === EnumAutomationSchedulePreset.Weekly ||
  raw === EnumAutomationSchedulePreset.Monthly
    ? (raw as EnumAutomationSchedulePreset)
    : EnumAutomationSchedulePreset.Daily;

const toAction = (raw: string): EnumAutomationAction | null =>
  (Object.values(EnumAutomationAction) as string[]).includes(raw)
    ? (raw as EnumAutomationAction)
    : null;

const isAction = (action: EnumAutomationAction | null): action is EnumAutomationAction =>
  action !== null;

export const toAutomationSchedule = (
  raw: IAutomationScheduleRaw
): IAutomationSchedule => ({
  preset: toPreset(raw.preset),
  daysOfWeek: raw.days_of_week ?? [],
  dayOfWeek: raw.day_of_week ?? null,
  dayOfMonth: raw.day_of_month ?? null,
  time: raw.time,
  timezone: raw.timezone,
  humanDescription: raw.human_description ?? '',
  nextRunAt: raw.next_run_at ?? null
});

export const toAutomationLastRun = (
  raw: IAutomationLastRunRaw
): IAutomationLastRun => ({
  status: toRunStatus(raw.status),
  finishedAt: raw.finished_at
});

export const toAutomation = (raw: IAutomationRaw): IAutomation => ({
  id: raw.id,
  title: raw.title,
  description: raw.description,
  status: toStatus(raw.status),
  provisioningStatus: toProvisioningStatus(raw.provisioning_status),
  provisioningError: raw.provisioning_error,
  schedule: toAutomationSchedule(raw.schedule),
  nextRunAt: raw.next_run_at,
  lastRun: raw.last_run ? toAutomationLastRun(raw.last_run) : null,
  allowedActions: raw.allowed_actions.map(toAction).filter(isAction),
  version: raw.version,
  createdAt: raw.created_at,
  updatedAt: raw.updated_at,
  pausedAt: raw.paused_at
});

export const toAutomationList = (raw: IAutomationListRaw): IAutomationList => ({
  items: raw.items.map(toAutomation)
});

export const toAutomationRunAccepted = (
  raw: IAutomationRunAcceptedRaw
): IAutomationRunAccepted => ({
  id: raw.id,
  automationId: raw.automation_id,
  status: raw.status,
  queuedAt: raw.queued_at
});

/* eslint-disable @typescript-eslint/naming-convention */
export const toAutomationScheduleRaw = (
  schedule: IAutomationScheduleRequest
): IAutomationScheduleRaw => ({
  preset: schedule.preset,
  ...(schedule.daysOfWeek !== undefined && { days_of_week: schedule.daysOfWeek }),
  ...(schedule.dayOfWeek !== undefined && { day_of_week: schedule.dayOfWeek }),
  ...(schedule.dayOfMonth !== undefined && { day_of_month: schedule.dayOfMonth }),
  time: schedule.time,
  timezone: schedule.timezone
});

export const toCreateAutomationRaw = (
  request: ICreateAutomationRequest
): Record<string, unknown> => ({
  title: request.title,
  description: request.description,
  schedule: toAutomationScheduleRaw(request.schedule),
  active: request.active
});

export const toUpdateAutomationRaw = (
  request: IUpdateAutomationRequest
): Record<string, unknown> => ({
  version: request.version,
  ...(request.title !== undefined && { title: request.title }),
  ...(request.description !== undefined && { description: request.description }),
  ...(request.schedule !== undefined && {
    schedule: toAutomationScheduleRaw(request.schedule)
  })
});
/* eslint-enable @typescript-eslint/naming-convention */
